import moment from 'moment';

import callApi from '../../util/apiCaller';
import { uploadFile } from '../../util/file';
import { capitalizeFirstLetter } from '../../util/text';

import { sendSocket, subscribeToChannel, unsubscribeToChannel } from '../App/AppActions';
import { addError } from '../Error/ErrorActions';

// Export Constants
export const SET_CONVERSATIONS = 'SET_CONVERSATIONS';
export const ADD_CONVERSATIONS = 'ADD_CONVERSATIONS';
export const UPDATE_CONVERSATION = 'UPDATE_CONVERSATION';
export const REMOVE_CONVERSATION = 'REMOVE_CONVERSATION';
export const SET_CONVERSATION_MESSAGES = 'SET_CONVERSATION_MESSAGES';
export const ADD_CONVERSATION_MESSAGE = 'ADD_CONVERSATION_MESSAGE';
export const SET_MESSAGE_WRITTING = 'SET_MESSAGE_WRITTING';
export const SET_SENDING_MESSAGE = 'SET_SENDING_MESSAGE';

export const CONVERSATION_STATUS_OPEN = 'open';
export const CONVERSATION_STATUS_CLOSED = 'closed';

// Export Actions
export function getConversationsRequest(needJoin = true) {
	return dispatch => {
		return callApi('conversations/open').then(res => {
			if(res.conversations) {
				dispatch(addConversations(res.conversations));
				needJoin && res.conversations.forEach(conversation => {
					dispatch(joinConversation(conversation._id));
				});
			}
			return res && res.conversations;
		}).catch(error => {
			dispatch(addError(error));
		});
	};
}

export function getConversationsBySessionRequest(sessionId, needJoin = true) {
	return dispatch => {
		return callApi(`session/${sessionId}/conversations`).then(res => {
			if(res.conversations) {
				dispatch(addConversations(res.conversations));
				needJoin && res.conversations.forEach(conversation => {
					dispatch(joinConversation(conversation._id));
				});
			}
			return res && res.conversations;
		}).catch(error => {
			dispatch(addError(error));
		});
	};
}

export function getConversationRequest(conversationId, loadMessages = false) {
	return dispatch => {
		return callApi(`conversation/${conversationId}`).then(res => {
			if(res.conversation) {
				dispatch(addConversations([res.conversation]));
				dispatch(updateConversation(res.conversation));
				loadMessages && dispatch(getConversationMessagesRequest(res.conversation._id));
			}
			return res.conversation;
		}).catch(error => {
			dispatch(addError(error));
			return null;
		});
	};
}

export function getConversationsByTargetRequest(targetType, target, participants = [], needJoin = true, includeMessages = false) {
	return dispatch => {
		let participantsQueryParam = '';
		if(participants && participants.length) {
			participantsQueryParam += '?';
			participants.forEach((participant, index) => {
				if(index > 0) {
					participantsQueryParam += '&';
				}
				participantsQueryParam += `participants[]=${participant}`;
			});
		}
		return callApi(`conversation/target/${capitalizeFirstLetter(targetType.toLowerCase())}/${target}${participantsQueryParam}`).then(res => {
			if(res.conversations) {
				dispatch(addConversations(res.conversations));
				includeMessages && res.conversations.forEach(conversation => dispatch(getConversationMessagesRequest(conversation._id)));
				needJoin && res.conversations.forEach(conversation => dispatch(joinConversation(conversation._id)));
			}
			return res.conversations;
		}).catch(error => {
			// dispatch(addError(error));
			return null;
		});
	};
}

export function getConversationMessagesRequest(conversationId) {
	return dispatch => {
		return callApi(`conversation/messages/${conversationId}`).then(res => {
			if(res.messages) {
				dispatch(setConversationMessages(conversationId, res.messages));
			}
		}).catch(error => {
			dispatch(addError(error));
		});
	};
}

export function editConversationRequest(conversation) {
	return dispatch => {
		return callApi('conversation/edit', 'post', { conversation }).then(res => {
			if(res.conversation) {
				dispatch(updateConversation(conversation));
			}
			return res.conversation;
		}).catch(error => {
			// dispatch(addError(error));
			return null;
		});
	};
}

export function addMessageRequest(message) {
	return dispatch => {
		return callApi('conversation/addmessage', 'post', { message }).then(res => {
			if(res.message) {
				dispatch(addConversationMessage(res.message));
				// if(res.message.type == 'user') {
				dispatch(sendSocket({ type: 'conversation/message/add', data: { message: res.message } }));
				// }
			}
			return res.message;
		}).catch(error => {
			dispatch(addError(error));
			return null;
		});
	};
}

export function addFileMessageRequest(user, message, data, contentType) {
	return dispatch => {
		return uploadFile(message.file, data, contentType, user._id ? user._id : user).then(result => {
			if(result.isOk) {
				dispatch(addMessageRequest(message));
				return message;
			}
			return null;
		}).catch(error => {
			dispatch(addError(error));
			return null;
		});
	};
}

export function setConversationViewRequest(conversationId) {
	return dispatch => {
		return callApi(`conversation/${conversationId}/view`).then(res => {
			if(res.conversation) {
				dispatch(updateConversation(res.conversation));
			}
		}).catch(error => {
			dispatch(addError(error));
		});
	};
}

export function setConversationView(conversation, participant) {
	return dispatch => {
		conversation = {
			...conversation,
			participantsViews: {
				...(conversation.participantsViews || {}),
				[participant._id]: moment.utc().format(),
			},
		};
		dispatch(updateConversation(conversation));
	};
}

export function joinConversation(conversationId) {
	return dispatch => {
		// dispatch(sendSocket({ type: 'subscribe', data: { channel: `conversation/${conversationId}` } }));
		dispatch(subscribeToChannel(`conversation/${conversationId}`));
	};
}

export function leaveConversation(conversationId) {
	return dispatch => {
		// dispatch(sendSocket({ type: 'subscribe', data: { channel: `conversation/${conversationId}` } }));
		dispatch(unsubscribeToChannel(`conversation/${conversationId}`));
	};
}

// Getters
export function getConversations(store, participant = null) {
	return store.conversations.data.filter(conversation => !participant || isUserInConversationParticipants(conversation, participant));
}

export function getConversation(store, conversationId) {
	return store.conversations.data.find(conversation => conversation._id === conversationId);
}

export function getConversationsByApplicant(store, applicant) {
	if(applicant) {
		let targetId = applicant._id || applicant;
		if(applicant.applicantGroup && applicant.applicantGroup.leader) {
			targetId = applicant.applicantGroup.leader;
		}
		return store.conversations.data.filter(conversation => conversation.targetType === 'Applicant' && getConversationTargetId(conversation) === targetId);
	}
	return [];
}

export function getConversationsByApplicants(store, applicants) {
	return applicants.map(applicant => getConversationsByApplicant(store, applicant)).reduce((all, conversations) => all.concat(conversations), []).filter(conversation => conversation);
}

export function getConversationOption(conversation, option) {
	if(conversation && conversation.options) {
		return conversation.options[option];
	}
	return '';
}

export function getConversationTargetId(conversation) {
	return conversation && conversation.target && (conversation.target._id || conversation.target);
}

export function getConversationParticipantIds(conversation) {
	return ((conversation && conversation.participants) || []).map(participant => getConversationParticipantId(participant));
}

export function getConversationParticipantId(participant) {
	return participant && (participant._id || participant);
}

export function getMessageAuthorId(message) {
	return message && message.author && (message.author._id || message.author);
}

export function getConversationName(conversation, currentUser = null) {
	const participants = (conversation.participants || []).filter(participant => !currentUser || currentUser._id !== participant._id);
	if(!participants.length) {
		return null;
	}
	if(participants.length > 3) {
		const firstNames = participants.slice(0, 2).map(participant => `${participant.firstName ? participant.firstName.charAt(0) : ''}. ${participant.lastName}`).join(', ');
		return `${firstNames}, + ${participants.length - 2}`;
	}
	return participants.map(participant => `${participant.firstName && participant.lastName ? `${participant.firstName.charAt(0)}.` : `${participant.firstName || ''}`} ${participant.role === 'student' || (currentUser && currentUser.role !== 'student') ? (participant.lastName || '') : ''}`).join(', ');
}

export function isUserInConversationParticipants(conversation, user) {
	return conversation && user && conversation.participants && conversation.participants.length && getConversationParticipantIds(conversation).includes(user._id || user);
}

export function getUnreadMessages(conversations, participant, forceParticipation = false) {
	return conversations.reduce((acc, conversation) => {
		if(conversation && participant && conversation.participants && (forceParticipation || isUserInConversationParticipants(conversation, participant))) {
			const participantId = (participant && participant._id) || participant;
			// Non participants (ex: proctors) views are stored too
			const participantLastView = (conversation.participantsViews || {})[participantId] || null;
			const otherParticipantsMessages = conversation.messages
				? conversation.messages
					.filter(message => getMessageAuthorId(message) !== participantId)
					.sort((messageA, messageB) => new Date(messageA.date) - new Date(messageB.date))
				: [];
			if(otherParticipantsMessages?.length) {
				const unreadMessages = otherParticipantsMessages.filter(message => {
					return (
						!message.hideToRoles.includes(participant.role)
						&& (
							!participantLastView
							|| moment(message.date).isAfter(moment(participantLastView))
						)
					);
				});
				return [...acc, ...unreadMessages];
			}
		}
		return acc;
	}, []);
}

export function getUnreadMessagesCount(conversations, participant, forceParticipation = false) {
	return getUnreadMessages(conversations, participant, forceParticipation).length || 0;
}

export function canUserSeeMessage(message, user) {
	if(user && message) {
		return !(message.hideToRoles || []).includes(user.role);
	}
	return false;
}



// Build the conversation message describing a capture frame. `media` is 'camera' or
// 'screen'; `intervalSeconds` is the effective capture cadence (sub-minute bursts are
// flagged `hidden` so they are collapsed out of the chat and shown in the viewer).
export function getNewCaptureMessage(conversationId, userId, media, intervalSeconds) {
	const prefix = media === 'screen' ? 'capture_screen' : 'capture_face';
	return {
		conversation: conversationId,
		file: `conversations/${conversationId}/${prefix}_${userId}_${moment().utc().format('X')}.jpg`,
		hideToRoles: ['student'],
		data: {
			isCapture: true,
			captureMedia: media,
			...(intervalSeconds < 60 ? { hidden: true } : {}),
		},
	};
}

export function setConversations(conversations) {
	return {
		type: SET_CONVERSATIONS,
		conversations,
	};
}

export function addConversations(conversations) {
	return {
		type: ADD_CONVERSATIONS,
		conversations,
	};
}

export function updateConversation(conversation) {
	return {
		type: UPDATE_CONVERSATION,
		conversation,
	};
}

export function removeConversation(conversationId) {
	return {
		type: REMOVE_CONVERSATION,
		conversationId,
	};
}

export function setConversationMessages(conversationId, messages) {
	return {
		type: SET_CONVERSATION_MESSAGES,
		conversationId,
		messages,
	};
}

export function addConversationMessage(message) {
	return {
		type: ADD_CONVERSATION_MESSAGE,
		message,
	};
}

export function setMessageWritting(user, conversationId, isWritting) {
	return {
		type: SET_MESSAGE_WRITTING,
		user,
		conversationId,
		isWritting,
	};
}
